import { useEffect, useState } from "react";

import {
  Button,
  InfoPanel,
  useStepperDispatch,
  Tooltip,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { ReactComponent as RemoveIcon } from "@king-ict/eupisi-ui/components/Icon/Remove.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../../app/store";
import QuestionDisplayTypeEnum from "../../../models/Questionnaire/QuestionDisplayTypeEnum";
import InitialConversationQuestionDataItem from "../../../models/Request/InitialConversationQuestionDataItem";
import InitialConversationQuestionDataItemValidationSchema from "../../../models/Request/InitialConversationQuestionDataItemValidationSchema";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import {
  updateRequestInitialConversationQuestionGroup,
  getExistingInitialConversation,
  selectAllDocuments,
} from "../../../slices/requestSlice";
import styles from "./InitialConversationStep.module.scss";
import QuestionComponentFactory from "./QuestionComponentFactory";
import { useInitialConversationStep } from "./useInitialConversationStep";

interface Props {
  subStep: number;
}

export default function InitialConversationStep({ subStep }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [initialQuestions, setInitialQuestions] = useState<
    InitialConversationQuestionDataItem[]
  >([]);

  const [
    isExistingAnswersButtonVisible,
    setIsExistingAnswersButtonVisible,
  ] = useState(false);

  const [openDocumentsModal, toggleDocumentsModal] = useState(false);
  const allDocuments = useSelector(selectAllDocuments);

  const [
    questionGroup,
    questionGroupConversationItems,
    setQuestionGroupConversationItems,
  ] = useInitialConversationStep({
    subStep: subStep,
  });

  useEffect(() => {
    dispatch(getExistingInitialConversation(false))
      .then(unwrapResult)
      .then(({ data: items }) => {
        setIsExistingAnswersButtonVisible(items.length > 0);
      });
  }, [dispatch]);

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleStep();
    },
  });

  useStepperDispatch({
    type: "previous",
    payload: () => {
      return handleStep();
    },
  });

  useStepperDispatch({
    type: "save",
    payload: () => {
      dispatch(
        updateRequestInitialConversationQuestionGroup({
          questionGroup: questionGroup!,
          initialConversationQuestionsData: questionGroupConversationItems,
        })
      );
    },
  });

  const [errors, doValidations, stopValidations] = useValidation({
    data: questionGroupConversationItems,
    validationSchema: InitialConversationQuestionDataItemValidationSchema,
  });

  const handleStep = () => {
    if (questionGroupConversationItems) {
      setInitialQuestions([]);
      const isValid = doValidations();
      if (isValid) {
        stopValidations();
        dispatch(
          updateRequestInitialConversationQuestionGroup({
            questionGroup: questionGroup!,
            initialConversationQuestionsData: questionGroupConversationItems,
          })
        );
      } else {
        return isValid;
      }
    } else {
      return false;
    }
  };

  const handleToggleDocumentsModal = () => {
    toggleDocumentsModal((openDocumentsModal) => !openDocumentsModal);
  };

  const handleToggleDocuments = (
    documents: RequestDocumentData[],
    stateIndex: number
  ) => {
    let questionGroupQuestionId =
      questionGroup?.questions[stateIndex + 1].questionGroupQuestionId;

    const newQuestionGroupConversationData: InitialConversationQuestionDataItem[] = [];

    questionGroupConversationItems.forEach((itemData) => {
      if (itemData.questionGroupQuestionId === questionGroupQuestionId) {
        const prevDocuments = itemData.documents ? [...itemData.documents] : [];
        newQuestionGroupConversationData.push({
          ...itemData,
          documents: [...prevDocuments, ...documents],
        });
      } else {
        newQuestionGroupConversationData.push({ ...itemData });
      }
    });

    setQuestionGroupConversationItems(newQuestionGroupConversationData);

    toggleDocumentsModal((openDocumentsModal) => !openDocumentsModal);
  };

  const handleRemoveDocumentFromList = (
    stateIndex: number,
    document: RequestDocumentData
  ) => {
    const selectQuestionGroupConversationItem = questionGroupConversationItems.find(
      (cr, index) => index === stateIndex
    );
    const newDocuments: RequestDocumentData[] = selectQuestionGroupConversationItem?.documents
      ? [...selectQuestionGroupConversationItem?.documents]
      : [];

    var index = [...newDocuments].indexOf(document);
    newDocuments.splice(index, 1);

    setQuestionGroupConversationItems((prev) => {
      const tempQuestionGroupConversationItems = prev ? [...prev] : [];
      const newQuestionGroupConversationItems: InitialConversationQuestionDataItem[] = [];
      tempQuestionGroupConversationItems.forEach((cr, index) => {
        if (index === stateIndex) {
          newQuestionGroupConversationItems.push({
            ...cr,
            documents: newDocuments,
          });
        } else {
          newQuestionGroupConversationItems.push({ ...cr });
        }
      });
      dispatch(
        updateRequestInitialConversationQuestionGroup({
          questionGroup: questionGroup!,
          initialConversationQuestionsData: newQuestionGroupConversationItems,
        })
      );
      return newQuestionGroupConversationItems;
    });
  };

  const copyQuestionsWithoutAnswer = (
    question: InitialConversationQuestionDataItem
  ): InitialConversationQuestionDataItem => {
    const newRepeatableSort =
      questionGroupConversationItems
        .map((item) => item.repeatableSort)
        .reduce((a, b) => Math.max(a, b)) + 1;

    const newQuestion: InitialConversationQuestionDataItem = {
      ...question,
      answerId: undefined,
      answer: undefined,
      repeatableSort: newRepeatableSort,
    };
    return newQuestion;
  };

  const handleAddNewQuestions = () => {
    let newQuestionGroup: InitialConversationQuestionDataItem[] = [
      ...questionGroupConversationItems,
    ];
    if (initialQuestions.length === 0) {
      const initQuestions = questionGroupConversationItems.filter(
        (item) => item.repeatableSort === 0
      );
      initQuestions.forEach((q) => {
        newQuestionGroup.push(copyQuestionsWithoutAnswer(q));
      });
      setInitialQuestions(initQuestions);
    } else {
      initialQuestions.forEach((q) => {
        newQuestionGroup.push(copyQuestionsWithoutAnswer(q));
      });
    }
    setQuestionGroupConversationItems(newQuestionGroup);
  };

  const handleRemoveQuestionSets = () => {
    const initialSetLength = questionGroupConversationItems.filter(
      (item) => item.repeatableSort === 0
    ).length;

    const newQuestionGroup = questionGroupConversationItems.slice(
      0,
      -initialSetLength
    );

    if (newQuestionGroup.length > 0) {
      setQuestionGroupConversationItems(newQuestionGroup);
    }
  };

  const isRepeatableQuestionSet = () => {
    return questionGroupConversationItems.some((item) => item.isRepeatable);
  };

  const renderQuestions = () => {
    const repeatableSetLength = questionGroupConversationItems.filter(
      (item) => item.repeatableSort === 0
    ).length;

    return questionGroupConversationItems.map((cd, i) => {
      const question = questionGroup?.questions.find(
        (q) => q.questionGroupQuestionId === cd.questionGroupQuestionId
      );

      if (!question) {
        return null;
      }

      const isLastQuestionInRepeatableSet = (i + 1) % repeatableSetLength === 0;

      // TODO: Resolve error text in QuetionComponentFactory
      // See how to put error text for Radio input in validationSchema
      let errorText;
      if (errors !== undefined) {
        switch (question.displayTypeId) {
          case QuestionDisplayTypeEnum.Text:
          case QuestionDisplayTypeEnum.TextArea:
          case QuestionDisplayTypeEnum.Time:
          case QuestionDisplayTypeEnum.Date:
            errorText = errors[i]?.answer;
            break;
          case QuestionDisplayTypeEnum.Select:
            errorText = errors[i]?.answerId;
            break;
          case QuestionDisplayTypeEnum.Radio:
            errorText = errors[i]?.answerId
              ? t("validations.radioGroupRequired")
              : undefined;
            break;
          case QuestionDisplayTypeEnum.MultiSelect:
            errorText = errors[i]?.answerIds;
            break;
          case QuestionDisplayTypeEnum.Upload:
            errorText = errors[i]?.documents;
            break;
        }
      }

      return (
        <>
          <QuestionComponentFactory
            allDocuments={allDocuments}
            key={
              cd.isRepeatable
                ? `${cd.questionGroupQuestionId}_${cd.repeatableSort}`
                : cd.questionGroupQuestionId
            }
            stateIndex={i}
            question={question!}
            conversationItem={cd}
            setState={setQuestionGroupConversationItems}
            handleToggleDocumentsModal={handleToggleDocumentsModal}
            openDocumentsModal={openDocumentsModal}
            onCloseDocumentsModal={handleToggleDocumentsModal}
            onConfirmDocumentsModal={handleToggleDocuments}
            onRemoveDocumentFromList={handleRemoveDocumentFromList}
            errorText={errorText}
          />
          {isLastQuestionInRepeatableSet && (
            <Row>
              <Col xs={12} className={styles.bottomSpacing}>
                <hr />
              </Col>
            </Row>
          )}
        </>
      );
    });
  };

  return (
    <>
      <div className={styles.info}>
        <InfoPanel
          message={t("initialConversationStep.basicInformation")}
          color="success"
          fullWidth
        />
      </div>
      <Row className={styles.headerRow}>
        <Col xs={12} lg={6}>
          <Typography variant="mediumBold">{questionGroup?.name}</Typography>
        </Col>
        {isExistingAnswersButtonVisible && (
          <Col xs={12} lg={6} className={styles.existingAnswersContainer}>
            <Tooltip
              placement="bottom"
              overlay={t("initialConversationStep.loadExistingAnswersInfo")}
            >
              <IconWrapper color="primary">
                <InfoIcon />
              </IconWrapper>
            </Tooltip>
            <Button
              className={styles.existingAnswersButton}
              color="primary"
              outline
              onClick={() => {
                dispatch(getExistingInitialConversation(true));
              }}
            >
              {t("initialConversationStep.loadExistingAnswersButton")}
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col />
        <Col xs={12} lg={6}>
          {renderQuestions()}
        </Col>
        <Col />
      </Row>

      {isRepeatableQuestionSet() && (
        <Row>
          <Col />
          <Col xs={12} lg={6}>
            <Tooltip
              placement="bottom"
              overlay={
                <span>
                  {t("initialConversationStep.tooltipRemoveFamilyMember")}
                </span>
              }
            >
              <Button
                outline
                icon={<RemoveIcon />}
                className={styles.iconButton}
                onClick={handleRemoveQuestionSets}
              >
                {t("initialConversationStep.removeFamilyMember")}
              </Button>
            </Tooltip>
            <Tooltip
              placement="bottom"
              overlay={
                <span>
                  {t("initialConversationStep.tooltipAddFamilyMember")}
                </span>
              }
            >
              <Button
                outline
                icon={<AddIcon />}
                className={styles.iconButton}
                onClick={handleAddNewQuestions}
              >
                {t("initialConversationStep.addFamilyMember")}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      )}
    </>
  );
}
